import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { openExternal, TERMS_URL, PRIVACY_URL } from '../utils/openExternal';
import { FiUser, FiLogOut, FiChevronDown, FiTrash2, FiFileText, FiShield, FiZap, FiAward } from 'react-icons/fi';

export default function ProfileMenu() {
  const { user, signOut, deleteAccount } = useAuth();
  const { isPremium, showPricing } = useSubscription();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setShowDeleteConfirm(false);
        setConfirmText('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleDeleteAccount() {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account deleted');
      navigate('/login', { replace: true });
    } catch {
      // apiClient already toasted the error message
      setDeleting(false);
    }
  }

  const displayName = user?.signInDetails?.loginId?.split('@')[0] || 'User';

  return (
    <div className="profile-menu-wrapper" ref={menuRef}>
      <button className="profile-menu-btn" onClick={() => setOpen(!open)}>
        <div className="profile-avatar">
          <FiUser />
        </div>
        <span className="profile-name">{displayName}</span>
        <FiChevronDown className={`profile-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="profile-dropdown">
          <div className="profile-dropdown-header">
            <span className="profile-dropdown-email">
              {user?.signInDetails?.loginId || 'user@email.com'}
            </span>
            {isPremium && <span className="profile-premium-badge">Premium</span>}
          </div>
          <button
            className="profile-dropdown-item"
            onClick={() => { navigate('/profile'); setOpen(false); }}
          >
            <FiAward />
            <span>View Profile</span>
          </button>
          {!isPremium && (
            <button
              className="profile-dropdown-item profile-upgrade-item"
              onClick={() => { showPricing('generic'); setOpen(false); }}
            >
              <FiZap />
              <span>Upgrade to Premium</span>
            </button>
          )}
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => { openExternal(PRIVACY_URL); setOpen(false); }}
          >
            <FiShield />
            <span>Privacy Policy</span>
          </button>
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => { openExternal(TERMS_URL); setOpen(false); }}
          >
            <FiFileText />
            <span>Terms of Use</span>
          </button>
          <div style={{ borderTop: '1px solid var(--border-secondary)' }} />
          <button
            className="profile-dropdown-item danger"
            onClick={async () => {
              setOpen(false);
              try {
                await signOut();
                navigate('/login', { replace: true });
              } catch {
                // signOut already routed the error through AuthContext
              }
            }}
          >
            <FiLogOut />
            <span>Sign Out</span>
          </button>
          <div style={{ borderTop: '1px solid var(--border-secondary)' }} />
          {!showDeleteConfirm ? (
            <button
              className="profile-dropdown-item danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <FiTrash2 />
              <span>Delete Account</span>
            </button>
          ) : (
            <div className="profile-delete-confirm">
              <p className="profile-delete-warning">
                This action is permanent and cannot be undone. All your decks, cards,
                review history, and account data will be deleted.
              </p>
              <label className="profile-delete-label">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                className="profile-delete-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                disabled={deleting}
              />
              <div className="profile-delete-actions">
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setConfirmText('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={confirmText !== 'DELETE' || deleting}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
